// =============================================================================
// SimSense Platform — Public Store / Pricing Page
// =============================================================================
// Dedicated pricing page with feature comparison and FAQ.
// Accessible without authentication. Interlinks with Landing page.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Store.css";
import "./Landing.css";

const FAQ_ITEMS = [
  {
    q: "What file types does SimSense support?",
    a: "SimSense processes iRacing .ibt telemetry files. The desktop agent watches your telemetry folder and uploads new files automatically after each session. You can also drag-and-drop files through the web interface.",
  },
  {
    q: "Can I try Pro features before subscribing?",
    a: "Yes! When you sign up, you get a free trial of Pro features. After the trial ends, you can continue using SimSense for free with the core feature set, or subscribe to keep full coaching and advanced analysis.",
  },
  {
    q: "How does coaching work?",
    a: "After your session is processed, SimSense analyzes your telemetry data — braking points, throttle application, steering input, corner entry/exit speeds and more — then generates personalized coaching recommendations. Free users get basic observations and 3 full coaching reports per month. Pro users get unlimited coaching reports with corner-by-corner grading.",
  },
  {
    q: "Is my telemetry data private?",
    a: "Yes, if you opt into not sharing your telemetry or setups with the community, all of your data will be private to you. Likewise, if you are part of a team whose settings are private, yours will automatically be private too. Commercial setups with partners will always be protected.",
  },
  {
    q: "Can I cancel my Pro subscription anytime?",
    a: "Yes, absolutely. You can cancel anytime from your account settings. You'll keep Pro access through the end of your current billing period.",
  },
];

const COMPARISON_ROWS: { category?: string; feature: string; free: string | boolean; pro: string | boolean; note?: string }[] = [
  { category: "Telemetry", feature: "Session upload & processing", free: true, pro: true },
  { category: "", feature: "Lap-by-lap data extraction", free: true, pro: true },
  { category: "", feature: "Tire temperature & wear data", free: true, pro: true },
  { category: "", feature: "Stint breakdown & run averages", free: true, pro: true },
  { category: "Analysis", feature: "Basic lap comparison (2 laps)", free: true, pro: true },
  { category: "", feature: "Interactive track map", free: true, pro: true },
  { category: "", feature: "Corner-by-corner analysis", free: true, pro: true },
  { category: "", feature: "Corner grading system (A-F)", free: false, pro: true },
  { category: "", feature: "Fastest driver lap comparison", free: false, pro: true },
  { category: "Coaching", feature: "Basic coaching observations", free: true, pro: true },
  { category: "", feature: "Advanced coaching reports", free: "3 / month", pro: "Unlimited" },
  { category: "Community", feature: "Car and track community leaderboards", free: true, pro: true },
  { category: "", feature: "Team creation & collaboration", free: true, pro: true },
  { category: "", feature: "Team telemetry sharing", free: true, pro: true },
  { category: "", feature: "Weekly challenges", free: true, pro: true },
  { category: "Desktop", feature: "Desktop agent (auto-upload)", free: true, pro: true },
  { category: "", feature: "Background file watcher", free: true, pro: true },
];

export function Store() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = "Pricing | SimSense";
  }, []);

  return (
    <div className="store">
      {/* ---------- Navigation (reuse Landing nav classes) ---------- */}
      <nav className="landing-nav">
        <Link to="/" className="landing-logo" style={{ textDecoration: "none" }}>
          <div className="landing-logo-mark">S</div>
          <span className="landing-logo-text">SimSense</span>
        </Link>
        <div className="landing-nav-actions">
          <Link to="/login" className="landing-nav-link">Sign In</Link>
          <Link to="/signup" className="landing-nav-cta">Get Started Free</Link>
        </div>
      </nav>

      {/* ---------- Hero ---------- */}
      <section className="store-hero">
        <div className="landing-badge">Simple Pricing</div>
        <h1>
          Start Free,{" "}
          <span className="accent">Go Pro</span>{" "}
          When You're Ready
        </h1>
        <p className="store-hero-subtitle">
          Everything you need to analyze your iRacing telemetry is free.
          Upgrade to Pro for advanced coaching and deeper analysis.
        </p>
      </section>

      {/* ---------- Pricing Cards ---------- */}
      <section className="store-pricing">
        <div className="store-pricing-grid">
          {/* Free Tier */}
          <div className="store-price-card">
            <div className="store-tier-name">Free</div>
            <div className="store-tier-tagline">Core telemetry tools, forever free</div>
            <div className="store-price">$0<span> / month</span></div>
            <div className="store-price-note">No credit card required</div>
            <ul className="store-price-features">
              <li><span className="check">&#10003;</span> Unlimited session uploads</li>
              <li><span className="check">&#10003;</span> Full telemetry data extraction</li>
              <li><span className="check">&#10003;</span> Lap comparison with track map</li>
              <li><span className="check">&#10003;</span> Run averages & stints analysis</li>
              <li><span className="check">&#10003;</span> Tire temperature & wear data</li>
              <li><span className="check">&#10003;</span> Corner-by-corner breakdown</li>
              <li><span className="check">&#10003;</span> Car and track community leaderboards</li>
              <li><span className="check">&#10003;</span> Team creation & data sharing</li>
              <li><span className="check">&#10003;</span> Desktop program and web client</li>
              <li><span className="check">&#10003;</span> 6 month data retention</li>
              <li><span className="check">&#10003;</span> Basic coaching observations</li>
              <li><span className="check">&#10003;</span> 3 coaching reports / month</li>
            </ul>
            <Link to="/signup" className="store-price-btn secondary">
              Get Started
            </Link>
          </div>

          {/* Pro Tier */}
          <div className="store-price-card featured">
            <div className="store-tier-name">SimSense Pro</div>
            <div className="store-tier-tagline">For drivers serious about improvement</div>
            <div className="store-price">$5<span> / month</span></div>
            <div className="store-price-note">Cancel anytime</div>
            <ul className="store-price-features">
              <li><span className="check">&#10003;</span> Everything in Free, plus:</li>
              <li><span className="check">&#10003;</span> Unlimited advanced coaching reports</li>
              <li><span className="check">&#10003;</span> Corner grading system (A-F) per session</li>
              <li><span className="check">&#10003;</span> Fastest driver lap comparison</li>
            </ul>
            <Link to="/signup" className="store-price-btn primary">
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- Feature Comparison Table ---------- */}
      <section className="store-comparison">
        <div className="store-comparison-header">
          <h2>Feature Comparison</h2>
          <p>See exactly what's included in each plan</p>
        </div>

        <div className="store-table-wrapper">
          <table className="store-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th className="pro-col">Pro</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <>
                  {row.category && (
                    <tr key={`cat-${i}`} className="category-row">
                      <td colSpan={3}>{row.category}</td>
                    </tr>
                  )}
                  <tr key={i}>
                    <td>{row.feature}</td>
                    <td>
                      {typeof row.free === "boolean" ? (
                        row.free ? (
                          <span className="table-check">&#10003;</span>
                        ) : (
                          <span className="table-x">&mdash;</span>
                        )
                      ) : (
                        row.free
                      )}
                    </td>
                    <td>
                      {typeof row.pro === "boolean" ? (
                        row.pro ? (
                          <span className="table-check">&#10003;</span>
                        ) : (
                          <span className="table-x">&mdash;</span>
                        )
                      ) : (
                        row.pro
                      )}
                      {row.note && <span className="table-label">{row.note}</span>}
                    </td>
                  </tr>
                </>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="store-faq">
        <div className="store-faq-header">
          <h2>Frequently Asked Questions</h2>
          <p>Everything you need to know about SimSense</p>
        </div>

        <div className="store-faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <div
              key={i}
              className={`store-faq-item${openFaq === i ? " open" : ""}`}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <div className="store-faq-question">
                <h3>{item.q}</h3>
                <svg className="store-faq-chevron" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {openFaq === i && <p className="store-faq-answer">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="store-cta">
        <h2>Ready to Get Faster?</h2>
        <p>
          Join SimSense today and start turning raw telemetry into lap time improvements.
          Free forever. Upgrade when you're ready.
        </p>
        <Link to="/signup" className="landing-btn-primary">
          Create Your Free Account
        </Link>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="landing-footer">
        <div className="landing-footer-content">
          <div className="landing-footer-brand">SimSense</div>
          <div className="landing-footer-links">
            <Link to="/">Home</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/login">Sign In</Link>
            <Link to="/signup">Get Started</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} SimSense. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
